import * as React from 'react';
import {connect} from "react-redux";
import {Redirect} from 'react-router-dom';
import {RouteComponentProps, withRouter} from 'react-router-dom';
import {SUB_DIR, URL} from '../../Config/appConfig';
import {Auth, Credentials, UpdateUser, ReduxUpdatedState} from '../../Config/dataModel';
import Footer from '../Footer/Footer';
import authDispatch from '../../ReduxRequests/Dispatch/authDispatch';
import authState from '../../ReduxRequests/State/authState';
import {assertAllTrue, clearErrors} from '../../Util/errorUtil';
import ErrorHandler from '../ErrorHandler/ErrorHandler';
import SelectInsurer from './SelectInsurer';
import UnsubscriptionConfirmation from '../Unsubscription/UnsubscriptionConfirmation';
import Unsubscription from '../Unsubscription/Unsubscription';
import {setDefaultLanguage} from '../../Util/language';
import * as moment from 'moment';
import '../../style/login.css';

const logo = require('../../resource/img/logo.svg');
const btnSpinner = require('../../resource/img/button-loading.gif');

interface LoginProps {
    login: (credential: Credentials) => void,
    logout: () => void,
    updateUser: (data: UpdateUser) => void,
    updateUserState: () => boolean,
    auth: () => Auth,
    loginState: () => boolean,
    robotLoginState: () => ReduxUpdatedState,
    tdLoginState: () => ReduxUpdatedState,
    isInsurer: () => boolean
}

interface LoginState {
    username: string,
    password: string,
    loading: boolean,
    isLoggedIn: boolean,
    isLoggedOut: boolean,
    isRobot: boolean,
    isTd: boolean,
    userUpdated: boolean
}

class Login extends React.Component <RouteComponentProps & LoginProps, LoginState> {
    public componentMountedAt : moment.Moment = moment();

    public constructor(props: LoginProps) {
        super(props);
        this.state = {
            username: '',
            password: '',
            loading: false,
            isLoggedIn: !!this.props.auth(),
            isLoggedOut: false,
            isRobot: false,
            isTd: false,
            userUpdated: false
        };
        this.changeCredentials = this.changeCredentials.bind(this);
        this.clickLogin = this.clickLogin.bind(this);
        this.clickBack = this.clickBack.bind(this);
        this.clickForgotPassword = this.clickForgotPassword.bind(this);
        this.onError = this.onError.bind(this);

        setDefaultLanguage();
    }

    public componentWillReceiveProps(nextProps: LoginProps) {
        if (nextProps.loginState() !== this.props.loginState() && nextProps.loginState()) {
            this.setLoading(false);
            if (this.isSessionExpired()) this.props.history.push(URL.DASHBOARD);
            if (this.isUnsubscription()) this.unsubscribe();
            else this.changeStatus('isLoggedIn', true);
        }
        if (nextProps.robotLoginState() && nextProps.robotLoginState().updatedAt.isAfter(this.componentMountedAt)) {
            this.setLoading(false);
            this.setIsRobot(true);
        }
        if (nextProps.tdLoginState() && nextProps.tdLoginState().updatedAt.isAfter(this.componentMountedAt)) {
            this.setLoading(false);
            this.setIsTd(true);
        }
        if (nextProps.updateUserState() !== this.props.updateUserState() && nextProps.updateUserState()) {
            this.setLoading(false);
            this.setState({
                isLoggedIn: true,
                userUpdated: true
            });
        }
    }

    public setIsRobot(isRobot: boolean) {
        this.setState({isRobot});
    }

    public setIsTd(isTd: boolean) {
        this.setState({isTd});
    }

    public setLoading(loading: boolean) {
        this.setState({loading});
    }

    public resetLoginStatus() {
        this.setIsRobot(false);
        this.setIsTd(false);
    }

    public isSessionExpired() {
        return this.props.history.location.pathname.split('/')[2] === SUB_DIR.SESSION_EXPIRED;
    }

    public isUnsubscription() {
        return this.props.history.location.pathname.split('/')[2] === SUB_DIR.UNSUBSCRIBE;
    }

    public isAccessible(isSessionExpired: boolean, isUnsubscription: boolean) {
        if (isUnsubscription) {
            return this.state.isLoggedIn;
        }
        return !isSessionExpired;
    }

    public unsubscribe() {
        this.props.updateUser({receiveEmailNotification: false});
    }

    public goToLogin() {
        this.componentMountedAt = moment();
        if (this.props.history.location.pathname.split('/')[2] === SUB_DIR.LOGIN) this.resetLoginStatus();
        else this.props.history.push(URL.LOGIN);
    }

    public changeStatus(name: string, status: boolean) {
        this.setState({
            ...this.state,
            [name]: status
        });
    }

    public changeCredentials(event: React.ChangeEvent<HTMLInputElement>) {
        event.preventDefault();
        const target = event.target;
        this.setState({
            ...this.state,
            [target.name]: target.value
        });
    }

    public clickForgotPassword(event: React.MouseEvent<HTMLElement>) {
		event.preventDefault();
        this.props.history.push({
            pathname: URL.FORGOT_PASSWORD,
            state: {
                email: this.state.username
            }
        });
    }

    public clickLogin(event: React.MouseEvent<HTMLElement> | React.FormEvent<HTMLFormElement>) {
        event.preventDefault();
        clearErrors();
        const nonBlank = assertAllTrue([
            {code: 151, hasResult: this.state.username.length > 0},
            {code: 152, hasResult: this.state.password.length > 0}
        ]);
        if (!nonBlank) { return; }

        this.setLoading(true);
        const credentials = {
            email: this.state.username,
            password: this.state.password
        };
        this.props.login(credentials);
    }

    public clickBack(event: React.MouseEvent<HTMLElement>) {
        event.preventDefault();
        this.resetLoginStatus();
        this.componentMountedAt = moment();
    }

    public onError() {
        this.setLoading(false);
    }

    public getContentWithAuth(isUnsubscription: boolean) {
        if (isUnsubscription) {
            if (this.state.userUpdated) {
                return (<UnsubscriptionConfirmation 
                            readyToLogout={() => this.changeStatus('isLoggedOut', true)}
                            selectInsurer={() => this.goToLogin()}/>);
            }
            else {
                return (<Unsubscription
                            unsubscribe={() => this.unsubscribe()}
                            selectInsurer={() => this.goToLogin()}/>);
            }
        }
        else {
            return (this.getContentAfterLogin());
        }
    }

    public getContentAfterLogin() {
        if (this.props.auth().insurer) {
            return (<Redirect to={URL.DASHBOARD}/>);
        }
        else {
            if (!this.props.isInsurer() && this.props.loginState()) {
                return (<SelectInsurer/>);
            }
            else {
                this.props.logout();
                return null;
            }
        }
    }

    public getInfoContent(isSessionExpired: boolean, isUnsubscription: boolean) {
        if (isSessionExpired) {
            return (<div className="cmText subscriptionLabel">Your session has expired due to inactivity.<br/>Please login again.</div>);
        }
        else if (isUnsubscription) {
            return (<div className="cmText subscriptionLabel">Login to unsubscribe daily activity report</div>);
        }
        else {
            return (<div className="cmText loginLabel">Login</div>);
        }
    }

    public render() {
        const isSessionExpired = this.isSessionExpired();
        const isUnsubscription = this.isUnsubscription();
        return (
            <div>
                <div className="authContainer">
                {this.props.auth() && this.isAccessible(isSessionExpired, isUnsubscription) ?
                    this.getContentWithAuth(isUnsubscription) :
                    (isUnsubscription && this.state.isLoggedOut ?
                        <Redirect to={URL.LOGIN}/> :
                        <div className="cmCenter loginContainer">
                            <button className="cmHorCenter" onClick={(e) => this.goToLogin()}>
                                <img src={logo}/>
                            </button>
                            {this.getInfoContent(isSessionExpired, isUnsubscription)}
                            {this.state.isRobot || this.state.isTd ?
                                <div>
                                    <div className="cmText cmHorCenter authInfoText">
                                        {this.state.isRobot ? 
                                            <span className="cmCenterBtn">Your account is not allowed to access to the portal.</span> :
                                            <span>This account is linked with the TD account, please access MIDAS through the TD App.</span>}
                                    </div>
                                    <div className="cmHorCenter authBtnsContainer">
                                        <button className="cmBtn cmCenterBtn cmBlueBtn cmBoldText" onClick={this.clickBack}>Back to Login</button>
                                    </div>
                                </div> :
                                <form onSubmit={this.clickLogin}>
                                    <div className="loginArea">
                                        <input className="cmLabel cmText cmInput loginInput" type="text" placeholder="Username" name="username"
                                            value={this.state.username} onChange={this.changeCredentials}/>
                                        <input className="cmLabel cmText cmInput loginInput" type="password" name="password"
                                            placeholder="Password" value={this.state.password}
                                            onChange={this.changeCredentials}/>
                                    </div>
                                    <ErrorHandler onError={this.onError}/>
                                    <div className="cmBtnsContainer">
                                        <div className="cmBtnWrapper">
                                            <button type="button" className="cmBtn cmGreyBtn cmBoldText" disabled={this.state.loading} onClick={this.clickForgotPassword}>Forgot password</button>
                                        </div>
                                        <div className="cmBtnWrapper">
                                            <button className="cmBtn cmBlueBtn cmBoldText" onClick={this.clickLogin}
                                                    disabled={this.state.loading}>
                                                <span className="cmBtnText">Submit</span>
                                                {this.state.loading && <img className="cmBtnSpinner" src={btnSpinner}/>}
                                            </button>
                                        </div>
                                    </div>
                                </form>}
                        </div>)}
                </div>
                <div className="authFooter"><Footer/></div>
            </div>
        );
    }
}

export default withRouter(connect(authState, authDispatch)(Login));



// WEBPACK FOOTER //
// ./src/Components/Auth/Login.tsx